<template>
	<div v-if="canSearch" class="header-search" ref="wrapRef">
		<ion-searchbar
			v-model="query"
			:placeholder="placeholder"
			debounce="400"
			show-clear-button="focus"
			inputmode="search"
			@ionInput="onInput"
			@ionClear="clearResults"
			@ionFocus="onFocus"
			@keyup.enter="onInput"
		></ion-searchbar>

		<teleport to="body">
			<div
				v-if="panelOpen"
				class="header-search-panel"
				:style="panelStyle"
			>
				<div v-if="loading" class="header-search-status">
					<ion-spinner name="crescent"></ion-spinner>
					<span>Buscando…</span>
				</div>

				<template v-else-if="hasResults">
					<ion-list lines="full">
						<template v-if="results.entidades.length">
							<ion-item-divider>
								<ion-label>Entidades</ion-label>
							</ion-item-divider>
							<ion-item
								v-for="item in results.entidades"
								:key="'e-' + item.id"
								button
								@click="goEntidad(item)"
							>
								<ion-icon :icon="peopleOutline" slot="start" color="primary"></ion-icon>
								<ion-label>
									<h3>{{ item.nombre }}</h3>
									<p v-if="entidadSubtitle(item)">{{ entidadSubtitle(item) }}</p>
								</ion-label>
							</ion-item>
						</template>

						<template v-if="results.procesos.length">
							<ion-item-divider>
								<ion-label>Procesos</ion-label>
							</ion-item-divider>
							<ion-item
								v-for="item in results.procesos"
								:key="'p-' + item.id"
								button
								@click="goProceso(item.id)"
							>
								<ion-icon :icon="gitBranchOutline" slot="start" color="primary"></ion-icon>
								<ion-label>
									<h3>{{ item.title }}</h3>
									<p v-if="item.estado">{{ item.estado }}</p>
								</ion-label>
							</ion-item>
						</template>

						<template v-if="results.incidencias.length">
							<ion-item-divider>
								<ion-label>Incidencias</ion-label>
							</ion-item-divider>
							<ion-item
								v-for="item in results.incidencias"
								:key="'i-' + item.id"
								button
								@click="goIncidencia(item.id)"
							>
								<ion-icon :icon="alertCircleOutline" slot="start" color="danger"></ion-icon>
								<ion-label>
									<h3>{{ item.title }}</h3>
									<p v-if="item.estado">{{ item.estado }}</p>
								</ion-label>
							</ion-item>
						</template>
					</ion-list>
				</template>

				<div v-else-if="searched" class="header-search-status">
					Sin resultados para «{{ query.trim() }}»
				</div>
			</div>
		</teleport>
	</div>
</template>

<script lang="ts" setup>
import { IonSearchbar, IonList, IonItem, IonItemDivider, IonLabel, IonIcon, IonSpinner } from '@ionic/vue'
import { peopleOutline, gitBranchOutline, alertCircleOutline } from 'ionicons/icons'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { currentUserStore } from '@/stores/currentUser'
import { SearchService, BusquedaEntidad, BusquedaGlobal } from '@/services/SearchService'

const router = useRouter()
const userStore = currentUserStore()
const wrapRef = ref<HTMLElement | null>(null)
const query = ref('')
const loading = ref(false)
const searched = ref(false)
const panelOpen = ref(false)
const panelStyle = ref<Record<string, string>>({})
const results = ref<BusquedaGlobal>({
	entidades: [],
	procesos: [],
	incidencias: [],
})

const canEntidades = computed(() => userStore.permissionNames.includes('user list'))
const canProcesos = computed(() => userStore.permissionNames.includes('procesos list'))
const canIncidencias = computed(() => userStore.permissionNames.includes('incidencias list'))
const canSearch = computed(() => canEntidades.value || canProcesos.value || canIncidencias.value)

const placeholder = computed(() => {
	const parts: string[] = []
	if (canEntidades.value) parts.push('entidades')
	if (canProcesos.value) parts.push('procesos')
	if (canIncidencias.value) parts.push('incidencias')
	if (parts.length === 0) return 'Buscar'
	if (parts.length === 1) return `Buscar ${parts[0]}…`
	return `Buscar ${parts.slice(0, -1).join(', ')} o ${parts[parts.length - 1]}…`
})

const hasResults = computed(() =>
	results.value.entidades.length + results.value.procesos.length + results.value.incidencias.length > 0
)

function entidadSubtitle(item: BusquedaEntidad): string {
	return [item.nif, item.mainTlf, item.mainEmail].filter(Boolean).join(' · ')
}

function positionPanel() {
	const el = wrapRef.value
	if (!el) return
	const rect = el.getBoundingClientRect()
	const left = Math.max(8, rect.left)
	const width = Math.min(rect.width, window.innerWidth - left - 8)
	panelStyle.value = {
		top: `${Math.round(rect.bottom + 4)}px`,
		left: `${Math.round(left)}px`,
		width: `${Math.round(Math.max(width, 240))}px`,
	}
}

async function onInput(ev?: CustomEvent) {
	const term = String(ev?.detail?.value ?? query.value).trim()
	if (term.length < 3) {
		clearResults()
		return
	}
	positionPanel()
	panelOpen.value = true
	loading.value = true
	searched.value = false
	try {
		results.value = await SearchService.buscar(term)
		searched.value = true
	} finally {
		loading.value = false
		positionPanel()
	}
}

function onFocus() {
	if (query.value.trim().length >= 3 && (hasResults.value || searched.value)) {
		positionPanel()
		panelOpen.value = true
	}
}

function clearResults() {
	results.value = { entidades: [], procesos: [], incidencias: [] }
	searched.value = false
	loading.value = false
	panelOpen.value = false
}

function closeAndReset() {
	query.value = ''
	clearResults()
}

function goEntidad(item: BusquedaEntidad) {
	const filtro = query.value.trim() || item.nombre
	closeAndReset()
	router.push({ path: '/organizacion/usuarios', query: { item: filtro } })
}

function goProceso(id: number) {
	closeAndReset()
	router.push({ name: 'Proceso', params: { proceso_id: String(id) } })
}

function goIncidencia(id: number) {
	closeAndReset()
	router.push({ path: '/organizacion/incidencias', query: { incidencia: String(id) } })
}

function onDocumentClick(event: MouseEvent) {
	const target = event.target as Node | null
	if (!target) return
	if (wrapRef.value?.contains(target)) return
	const panel = document.querySelector('.header-search-panel')
	if (panel?.contains(target)) return
	panelOpen.value = false
}

function onKeydown(event: KeyboardEvent) {
	if (event.key === 'Escape') {
		panelOpen.value = false
	}
}

onMounted(() => {
	document.addEventListener('click', onDocumentClick)
	window.addEventListener('keydown', onKeydown)
	window.addEventListener('resize', positionPanel)
})

onUnmounted(() => {
	document.removeEventListener('click', onDocumentClick)
	window.removeEventListener('keydown', onKeydown)
	window.removeEventListener('resize', positionPanel)
})
</script>

<style scoped>
.header-search {
	flex: 1 1 auto;
	min-width: 0;
	max-width: 720px;
	margin: 0 auto;
	padding: 0 4px;
}

.header-search ion-searchbar {
	padding: 0;
	--box-shadow: none;
	--background: var(--ion-color-light, #f4f5f8);
	--border-radius: 10px;
}
</style>

<style>
.header-search-panel {
	position: fixed;
	z-index: 20000;
	max-height: min(70vh, 480px);
	overflow: auto;
	background: var(--ion-background-color, #fff);
	border-radius: 10px;
	box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
}

.header-search-panel ion-item-divider {
	--background: var(--ion-color-light, #f4f5f8);
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.02em;
	text-transform: uppercase;
}

.header-search-panel h3 {
	margin: 0;
	font-size: 0.95rem;
}

.header-search-panel p {
	margin: 2px 0 0;
	font-size: 0.8rem;
	color: var(--ion-color-medium, #92949c);
}

.header-search-status {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 14px 16px;
	color: var(--ion-color-medium, #92949c);
	font-size: 0.9rem;
}
</style>
